import { create } from 'zustand';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  ANNUAL_INCOME_OPTIONS,
  EDUCATION_LEVELS,
  REGION_PREFECTURES,
  calculateDonorScore,
  type RegionName,
  type VerificationRank,
} from '@kulum/shared';

export type SearchSortBy =
  | 'recommended'
  | 'highSpec'
  | 'review'
  | 'verified'
  | 'nearby'
  | 'newest';

// Filter types
export interface SearchFilters {
  region?: RegionName;
  prefecture?: string;
  ageMin?: number;
  ageMax?: number;
  heightMin?: number;
  heightMax?: number;
  educationLevel?: string;
  bloodType?: string;
  donationMethod?: 'syringe' | 'timing';
  annualIncomeMin?: string;
  hasReviews?: boolean;
  minReviewRating?: number;
  identityVerified?: boolean;
  educationVerified?: boolean;
  incomeVerified?: boolean;
  spermTestVerified?: boolean;
  stdTestVerified?: boolean;
  donationArea?: string;
  travelExpense?: string;
  identityDisclosure?: string;
  bodyType?: string;
  maritalStatus?: string;
  sortBy?: SearchSortBy;
}

// Verification badges stored in profiles collection
export interface VerificationBadges {
  identity: boolean;
  education: boolean;
  income: boolean;
  spermTest: boolean;
  stdTest: boolean;
  ranks?: {
    education?: VerificationRank;
    income?: VerificationRank;
    spermTest?: VerificationRank;
    stdTest?: VerificationRank;
  };
}

// Donor summary for list display
export interface DonorSummary {
  userId: string;
  displayName: string;
  iconUrl?: string;
  age: number;
  residence: string;
  height?: number;
  weight?: number;
  educationLevel?: string;
  schoolName?: string;
  educationStatus?: string;
  annualIncome?: string;
  bodyType?: string;
  maritalStatus?: string;
  bloodType?: string;
  donationMethods: string[];
  donationAreas: string[];
  compensation?: number;
  travelExpense?: string;
  identityDisclosure?: string;
  freeText: string;
  score: number;
  verifications: {
    identity: boolean;
    education: boolean;
    income: boolean;
    spermTest: boolean;
    stdTest: boolean;
  };
  ranks: {
    education?: VerificationRank;
    income?: VerificationRank;
    spermTest?: VerificationRank;
    stdTest?: VerificationRank;
  };
  identityVerified: boolean;
  reviewStats: {
    averageRating: number;
    totalCount: number;
  };
  createdAt: Date;
}

// Donor detail (full profile)
export interface DonorDetail extends DonorSummary {
  basicInfo: {
    birthDate?: Date;
    nationality?: string;
    bloodType?: string;
    bloodTypeRh?: string;
    birthplace?: string;
    education?: {
      level?: string;
      schoolName?: string;
      faculty?: string;
      status?: string;
    };
    annualIncome?: string;
    maritalStatus?: string;
    children?: {
      sons: number;
      daughters: number;
    };
    ancestry?: string;
    vaccinations?: {
      influenza?: string;
      covid?: string;
    };
  };
  physicalInfo?: {
    height?: number;
    weight?: number;
    bodyType?: string;
    hairType?: string;
    hairColor?: string;
    eyelid?: string;
  };
  donationPolicy?: {
    methods?: string[];
    compensation?: number;
    travelExpense?: string;
    identityDisclosure?: string;
    consanguinityMeasures?: string;
    rightToKnowOrigin?: string;
    sameCityFamilyDonation?: string;
    donationAreas?: string[];
  };
  images?: string[];
}

interface SearchState {
  // State
  donors: DonorSummary[];
  selectedDonor: DonorDetail | null;
  filters: SearchFilters;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  error: string | null;
  doneePrefecture: string | null;

  // Actions
  setFilters: (filters: Partial<SearchFilters>) => void;
  resetFilters: () => void;
  setDoneePrefecture: (prefecture: string) => void;
  searchDonors: () => Promise<void>;
  loadMoreDonors: () => Promise<void>;
  fetchDonorDetail: (donorId: string) => Promise<void>;
  clearSelectedDonor: () => void;
  clearError: () => void;
  reset: () => void;
}

const DONORS_PER_PAGE = 60;
const SEARCHABLE_DONOR_STATUSES = ['email_verified', 'under_review', 'approved'] as const;

// Calculate age from birthDate
const calculateAge = (birthDate: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Get prefectures for a region
const getPrefecturesForRegion = (region: RegionName): readonly string[] => {
  return REGION_PREFECTURES[region] || [];
};

// Fetch review stats for a user
const fetchReviewStats = async (userId: string): Promise<{ averageRating: number; totalCount: number }> => {
  try {
    const reviewsRef = collection(db, 'reviews');
    const reviewsQuery = query(reviewsRef, where('toUserId', '==', userId));
    const snapshot = await getDocs(reviewsQuery);
    if (snapshot.empty) {
      return { averageRating: 0, totalCount: 0 };
    }
    let total = 0;
    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      const rating = typeof data.rating === 'number' ? data.rating : 0;
      total += rating;
    });
    const count = snapshot.docs.length;
    return {
      averageRating: Math.round((total / count) * 10) / 10,
      totalCount: count,
    };
  } catch {
    return { averageRating: 0, totalCount: 0 };
  }
};

// Default verification badges (all false)
const defaultVerificationBadges: VerificationBadges = {
  identity: false,
  education: false,
  income: false,
  spermTest: false,
  stdTest: false,
  ranks: {},
};

const getOptionScore = <T extends readonly string[]>(options: T, value?: string): number => {
  if (!value) return 0;
  const index = options.findIndex((option) => option === value);
  return index >= 0 ? index + 1 : 0;
};

const getRankScore = (rank?: VerificationRank | null): number => {
  const scores: Record<string, number> = {
    S: 12,
    A: 10,
    B: 7,
    C: 4,
    D: 2,
  };
  return rank ? scores[rank] || 0 : 0;
};

const getAnnualIncomeScore = (income?: string): number =>
  getOptionScore(ANNUAL_INCOME_OPTIONS, income);

const getEducationScore = (level?: string): number =>
  getOptionScore(EDUCATION_LEVELS, level);

const calculateHighSpecScore = (donor: DonorSummary): number => {
  let score = 0;

  score += getEducationScore(donor.educationLevel) * 6;
  score += getAnnualIncomeScore(donor.annualIncome) * 5;

  if (typeof donor.height === 'number') {
    if (donor.height >= 180) score += 14;
    else if (donor.height >= 175) score += 10;
    else if (donor.height >= 170) score += 6;
  }

  score += getRankScore(donor.ranks.education) * 2;
  score += getRankScore(donor.ranks.income) * 2;
  score += getRankScore(donor.ranks.spermTest);
  score += getRankScore(donor.ranks.stdTest);

  if (donor.verifications.identity || donor.identityVerified) score += 10;
  if (donor.verifications.education) score += 12;
  if (donor.verifications.income) score += 12;
  if (donor.verifications.spermTest) score += 6;
  if (donor.verifications.stdTest) score += 6;

  if (donor.reviewStats.totalCount > 0) {
    score += donor.reviewStats.averageRating * 3;
    score += Math.min(donor.reviewStats.totalCount, 10);
  }

  if (donor.schoolName) score += 3;
  if (donor.freeText.trim().length >= 80) score += 3;

  return score;
};

const calculateVerificationScore = (donor: DonorSummary): number => {
  let score = 0;
  if (donor.verifications.identity || donor.identityVerified) score += 12;
  if (donor.verifications.education) score += 10;
  if (donor.verifications.income) score += 10;
  if (donor.verifications.spermTest) score += 8;
  if (donor.verifications.stdTest) score += 8;
  score += getRankScore(donor.ranks.education);
  score += getRankScore(donor.ranks.income);
  score += getRankScore(donor.ranks.spermTest);
  score += getRankScore(donor.ranks.stdTest);
  return score;
};

const calculateReviewScore = (donor: DonorSummary): number => {
  if (donor.reviewStats.totalCount === 0) return 0;
  return donor.reviewStats.averageRating * 10 + Math.min(donor.reviewStats.totalCount, 20);
};

const getSortValue = (donor: DonorSummary, sortBy: SearchSortBy): number => {
  switch (sortBy) {
    case 'highSpec':
      return calculateHighSpecScore(donor);
    case 'review':
      return calculateReviewScore(donor);
    case 'verified':
      return calculateVerificationScore(donor);
    case 'nearby':
      return donor.score;
    case 'newest':
      return donor.createdAt.getTime();
    case 'recommended':
    default:
      return donor.score + calculateHighSpecScore(donor) * 0.45 + calculateReviewScore(donor) * 0.25;
  }
};

const compareDonorIdentity = (a: DonorSummary, b: DonorSummary): number =>
  a.displayName.localeCompare(b.displayName, 'ja') || a.userId.localeCompare(b.userId);

const sortDonors = (donors: DonorSummary[], sortBy: SearchSortBy = 'recommended'): DonorSummary[] => {
  return [...donors].sort((a, b) => {
    const scoreDiff = getSortValue(b, sortBy) - getSortValue(a, sortBy);
    if (scoreDiff !== 0) return scoreDiff;
    return compareDonorIdentity(a, b);
  });
};

const matchesFilters = (donor: DonorSummary, filters: SearchFilters): boolean => {
  const {
    ageMin,
    ageMax,
    annualIncomeMin,
    bloodType,
    bodyType,
    donationArea,
    donationMethod,
    educationLevel,
    educationVerified,
    hasReviews,
    heightMin,
    heightMax,
    identityDisclosure,
    identityVerified,
    incomeVerified,
    maritalStatus,
    minReviewRating,
    prefecture,
    region,
    spermTestVerified,
    stdTestVerified,
    travelExpense,
  } = filters;

  if (prefecture && donor.residence !== prefecture) return false;
  if (region && !prefecture) {
    const prefectures = getPrefecturesForRegion(region);
    if (prefectures.length > 0 && !prefectures.includes(donor.residence)) return false;
  }
  if (ageMin && donor.age < ageMin) return false;
  if (ageMax && donor.age > ageMax) return false;
  if (heightMin && (!donor.height || donor.height < heightMin)) return false;
  if (heightMax && (!donor.height || donor.height > heightMax)) return false;
  if (educationLevel && donor.educationLevel !== educationLevel) return false;
  if (annualIncomeMin && getAnnualIncomeScore(donor.annualIncome) < getAnnualIncomeScore(annualIncomeMin)) return false;
  if (bloodType && donor.bloodType !== bloodType) return false;
  if (bodyType && donor.bodyType !== bodyType) return false;
  if (maritalStatus && donor.maritalStatus !== maritalStatus) return false;
  if (donationMethod && !donor.donationMethods.includes(donationMethod)) return false;
  if (donationArea && !donor.donationAreas.includes(donationArea)) return false;
  if (travelExpense && donor.travelExpense !== travelExpense) return false;
  if (identityDisclosure && donor.identityDisclosure !== identityDisclosure) return false;
  if (hasReviews && donor.reviewStats.totalCount === 0) return false;
  if (minReviewRating && donor.reviewStats.averageRating < minReviewRating) return false;
  if (identityVerified && !(donor.verifications.identity || donor.identityVerified)) return false;
  if (educationVerified && !donor.verifications.education) return false;
  if (incomeVerified && !donor.verifications.income) return false;
  if (spermTestVerified && !donor.verifications.spermTest) return false;
  if (stdTestVerified && !donor.verifications.stdTest) return false;

  return true;
};

const hasFilteringConditions = (filters: SearchFilters): boolean => {
  return Object.entries(filters).some(([key, value]) => {
    if (key === 'sortBy') return false;
    return value !== undefined && value !== null && value !== '' && value !== false;
  });
};

const shouldScanAllCandidates = (filters: SearchFilters): boolean => {
  if (hasFilteringConditions(filters)) return true;
  return filters.sortBy !== 'newest';
};

const buildDonorSummary = async (
  userDoc: QueryDocumentSnapshot<DocumentData>,
  doneePrefecture: string | null
): Promise<DonorSummary> => {
  const userData = userDoc.data();
  const userId = userDoc.id;

  const profileRef = doc(db, 'profiles', userId);
  const [profileSnap, reviewStats] = await Promise.all([
    getDoc(profileRef),
    fetchReviewStats(userId),
  ]);
  const profileData = profileSnap.exists() ? profileSnap.data() : null;

  const badges: VerificationBadges = profileData?.verificationBadges || defaultVerificationBadges;
  const birthDate = profileData?.basicInfo?.birthDate?.toDate?.() || null;
  const age = birthDate ? calculateAge(birthDate) : 0;
  const ranks = badges.ranks || {};
  const donorResidence = profileData?.basicInfo?.residence || '';
  const score = doneePrefecture
    ? calculateDonorScore(donorResidence, doneePrefecture, ranks)
    : calculateVerificationScore({
        ranks,
        verifications: {
          identity: badges.identity,
          education: badges.education,
          income: badges.income,
          spermTest: badges.spermTest,
          stdTest: badges.stdTest,
        },
        identityVerified: userData.identityVerified === true,
      } as DonorSummary);

  return {
    userId,
    displayName: userData.displayName || 'Unknown',
    iconUrl: userData.iconUrl,
    age,
    residence: donorResidence,
    height: profileData?.physicalInfo?.height,
    weight: profileData?.physicalInfo?.weight,
    educationLevel: profileData?.basicInfo?.education?.level,
    schoolName: profileData?.basicInfo?.education?.schoolName,
    educationStatus: profileData?.basicInfo?.education?.status,
    annualIncome: profileData?.basicInfo?.annualIncome,
    bodyType: profileData?.physicalInfo?.bodyType,
    maritalStatus: profileData?.basicInfo?.maritalStatus,
    bloodType: profileData?.basicInfo?.bloodType,
    donationMethods: Array.isArray(profileData?.donationPolicy?.methods)
      ? profileData.donationPolicy.methods.filter((method: unknown): method is string => typeof method === 'string')
      : [],
    donationAreas: Array.isArray(profileData?.donationPolicy?.donationAreas)
      ? profileData.donationPolicy.donationAreas.filter((area: unknown): area is string => typeof area === 'string')
      : [],
    compensation: typeof profileData?.donationPolicy?.compensation === 'number'
      ? profileData.donationPolicy.compensation
      : undefined,
    travelExpense: profileData?.donationPolicy?.travelExpense,
    identityDisclosure: profileData?.donationPolicy?.identityDisclosure,
    freeText: typeof profileData?.freeText === 'string' ? profileData.freeText : '',
    score,
    verifications: {
      identity: badges.identity,
      education: badges.education,
      income: badges.income,
      spermTest: badges.spermTest,
      stdTest: badges.stdTest,
    },
    ranks,
    identityVerified: userData.identityVerified === true,
    reviewStats,
    createdAt: userData.createdAt?.toDate?.() || new Date(0),
  };
};

export const useSearchStore = create<SearchState>((set, get) => ({
  donors: [],
  selectedDonor: null,
  filters: {},
  isLoading: false,
  isLoadingMore: false,
  hasMore: false,
  lastDoc: null,
  error: null,
  doneePrefecture: null,

  setFilters: (newFilters: Partial<SearchFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
  },

  resetFilters: () => {
    set({ filters: {}, donors: [], hasMore: false, lastDoc: null });
  },

  setDoneePrefecture: (prefecture: string) => {
    set({ doneePrefecture: prefecture });
  },

  searchDonors: async () => {
    try {
      set({ isLoading: true, error: null, donors: [], hasMore: false, lastDoc: null });

      const { filters, doneePrefecture } = get();

      // Build query constraints
      const constraints: QueryConstraint[] = [
        where('role', '==', 'donor'),
        where('status', 'in', SEARCHABLE_DONOR_STATUSES),
      ];

      // Note: Firestore has limitations on compound queries
      // Age and height filtering will be done client-side for now
      
      constraints.push(orderBy('createdAt', 'desc'));

      // First, get searchable donors from users collection
      const usersRef = collection(db, 'users');
      const scanAllCandidates = shouldScanAllCandidates(filters);
      const matchedDonors: DonorSummary[] = [];
      let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
      let lastSnapshotDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      let hasMoreRawCandidates = true;

      while (hasMoreRawCandidates) {
        const pageConstraints: QueryConstraint[] = [...constraints];
        if (cursor) {
          pageConstraints.push(startAfter(cursor));
        }
        pageConstraints.push(limit(DONORS_PER_PAGE));

        const usersQuery = query(usersRef, ...pageConstraints);
        const usersSnapshot = await getDocs(usersQuery);

        if (usersSnapshot.empty) {
          break;
        }

        const snapshotDocs: QueryDocumentSnapshot<DocumentData>[] = usersSnapshot.docs;
        const donorPromises = snapshotDocs.map((userDoc) =>
          buildDonorSummary(userDoc, doneePrefecture)
        );
        const pageDonors = (await Promise.all(donorPromises)).filter((donor) =>
          matchesFilters(donor, filters)
        );
        matchedDonors.push(...pageDonors);

        lastSnapshotDoc = snapshotDocs[snapshotDocs.length - 1];
        cursor = lastSnapshotDoc;
        hasMoreRawCandidates = snapshotDocs.length === DONORS_PER_PAGE;

        if (!scanAllCandidates) {
          break;
        }
      }

      const donors = sortDonors(matchedDonors, filters.sortBy);
      const hasMore = scanAllCandidates ? false : hasMoreRawCandidates;

      set({
        donors,
        hasMore,
        lastDoc: scanAllCandidates ? null : lastSnapshotDoc,
      });
    } catch (error) {
      console.error('Error searching donors:', error);
      set({ error: 'ドナーの検索に失敗しました' });
    } finally {
      set({ isLoading: false });
    }
  },

  loadMoreDonors: async () => {
    const { lastDoc, hasMore, isLoadingMore, filters, doneePrefecture, donors } = get();

    if (shouldScanAllCandidates(filters)) return;
    if (!hasMore || isLoadingMore || !lastDoc) return;

    try {
      set({ isLoadingMore: true, error: null });

      const constraints: QueryConstraint[] = [
        where('role', '==', 'donor'),
        where('status', 'in', SEARCHABLE_DONOR_STATUSES),
      ];

      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(startAfter(lastDoc));
      constraints.push(limit(DONORS_PER_PAGE));

      const usersRef = collection(db, 'users');
      const usersQuery = query(usersRef, ...constraints);
      const usersSnapshot = await getDocs(usersQuery);

      if (usersSnapshot.empty) {
        set({ hasMore: false });
        return;
      }

      const donorPromises = usersSnapshot.docs.map((userDoc) =>
        buildDonorSummary(userDoc, doneePrefecture)
      );

      const newDonors = (await Promise.all(donorPromises)).filter((donor) =>
        matchesFilters(donor, filters)
      );
      const allDonors = sortDonors([...donors, ...newDonors], filters.sortBy);
      const newLastDoc = usersSnapshot.docs[usersSnapshot.docs.length - 1];
      const newHasMore = usersSnapshot.docs.length === DONORS_PER_PAGE;

      set({ donors: allDonors, hasMore: newHasMore, lastDoc: newLastDoc });
    } catch (error) {
      console.error('Error loading more donors:', error);
      set({ error: 'ドナーの読み込みに失敗しました' });
    } finally {
      set({ isLoadingMore: false });
    }
  },

  fetchDonorDetail: async (donorId: string) => {
    try {
      set({ isLoading: true, error: null, selectedDonor: null });

      // Get user
      const userRef = doc(db, 'users', donorId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        set({ error: 'ドナーが見つかりません' });
        return;
      }

      const userData = userSnap.data();

      // Get profile and review stats in parallel
      const profileRef = doc(db, 'profiles', donorId);
      const [profileSnap, reviewStats] = await Promise.all([
        getDoc(profileRef),
        fetchReviewStats(donorId),
      ]);
      const profileData = profileSnap.exists() ? profileSnap.data() : null;

      // Get verification badges from profile
      const badges: VerificationBadges = profileData?.verificationBadges || defaultVerificationBadges;

      // Calculate age
      const birthDate = profileData?.basicInfo?.birthDate?.toDate?.() || null;
      const age = birthDate ? calculateAge(birthDate) : 0;

      // Get ranks from badges
      const ranks = badges.ranks || {};

      // Calculate score
      const { doneePrefecture } = get();
      const donorResidence = profileData?.basicInfo?.residence || '';
      const score = doneePrefecture
        ? calculateDonorScore(donorResidence, doneePrefecture, ranks)
        : 0;

      const donorDetail: DonorDetail = {
        userId: donorId,
        displayName: userData.displayName || 'Unknown',
        iconUrl: userData.iconUrl,
        age,
        residence: donorResidence,
        height: profileData?.physicalInfo?.height,
        weight: profileData?.physicalInfo?.weight,
        educationLevel: profileData?.basicInfo?.education?.level,
        schoolName: profileData?.basicInfo?.education?.schoolName,
        educationStatus: profileData?.basicInfo?.education?.status,
        annualIncome: profileData?.basicInfo?.annualIncome,
        bloodType: profileData?.basicInfo?.bloodType,
        donationMethods: Array.isArray(profileData?.donationPolicy?.methods)
          ? profileData.donationPolicy.methods.filter((method: unknown): method is string => typeof method === 'string')
          : [],
        donationAreas: Array.isArray(profileData?.donationPolicy?.donationAreas)
          ? profileData.donationPolicy.donationAreas.filter((area: unknown): area is string => typeof area === 'string')
          : [],
        freeText: typeof profileData?.freeText === 'string' ? profileData.freeText : '',
        score,
        verifications: {
          identity: badges.identity,
          education: badges.education,
          income: badges.income,
          spermTest: badges.spermTest,
          stdTest: badges.stdTest,
        },
        ranks,
        identityVerified: userData.identityVerified === true,
        reviewStats,
        createdAt: userData.createdAt?.toDate?.() || new Date(),
        basicInfo: {
          birthDate,
          nationality: profileData?.basicInfo?.nationality,
          bloodType: profileData?.basicInfo?.bloodType,
          bloodTypeRh: profileData?.basicInfo?.bloodTypeRh,
          birthplace: profileData?.basicInfo?.birthplace,
          education: profileData?.basicInfo?.education,
          annualIncome: profileData?.basicInfo?.annualIncome,
          maritalStatus: profileData?.basicInfo?.maritalStatus,
          children: profileData?.basicInfo?.children,
          ancestry: profileData?.basicInfo?.ancestry,
          vaccinations: profileData?.basicInfo?.vaccinations,
        },
        physicalInfo: profileData?.physicalInfo,
        donationPolicy: profileData?.donationPolicy,
        images: profileData?.images,
      };

      set({ selectedDonor: donorDetail });
    } catch (error) {
      console.error('Error fetching donor detail:', error);
      set({ error: 'ドナー情報の取得に失敗しました' });
    } finally {
      set({ isLoading: false });
    }
  },

  clearSelectedDonor: () => {
    set({ selectedDonor: null });
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      donors: [],
      selectedDonor: null,
      filters: {},
      isLoading: false,
      isLoadingMore: false,
      hasMore: false,
      lastDoc: null,
      error: null,
      doneePrefecture: null,
    });
  },
}));
